import { ClientsCloseOverviewColumn } from "@common/types/views/clientsCloseOverviewView";
import { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import { ViewConfig } from "@lib/views";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import type { ClientOrgBillingTransferRequest } from "@common/schemas/client.schema";
import { EntityProfileType } from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    CloseOverviewEntityIcon,
    CloseOverviewEntityRow,
    CloseOverviewMember,
    CloseOverviewWorkspaceRow,
} from "@common/schemas/clientsCloseOverview.schema";
import { TeamCell } from "../clients/views/cells/TeamCell";
import { MembersCell } from "../clients/views/cells/MembersCell";
import { ClientNameCell } from "./cells/ClientNameCell";
import { EntitiesCell } from "./cells/EntitiesCell";
import { ClosePlanCell } from "./cells/ClosePlanCell";
import { CloseBillingOwnershipCell } from "./cells/CloseBillingOwnershipCell";
import { CloseLegalTypeCell } from "./cells/CloseLegalTypeCell";
import { CloseNoteCell } from "./cells/CloseNoteCell";
import { CloseServicesCell } from "./cells/CloseServicesCell";
import { CloseActionCell } from "./cells/CloseActionCell";
import { BookkeepingStartDateCell } from "./cells/BookkeepingStartDateCell";
import { CreatedAtCell } from "./cells/CreatedAtCell";

export interface ClientsCloseOverviewRow {
    id: string;
    rowType: "workspace" | "entity";
    type: "workspace" | "entity";
    name: string;
    // workspace fields
    teamMembers?: CloseOverviewMember[];
    accessMembers?: CloseOverviewMember[];
    team?: [];
    members?: CloseOverviewMember[];
    invites?: [];
    entities?: CloseOverviewEntityIcon[];
    plan?: FLAT_RATE_PLANS | null;
    isOrganizationBillingOwner?: boolean;
    isFirmWorkspace?: boolean;
    hasUsersToTransferBillingTo?: boolean;
    orgBillingTransferRequest?: ClientOrgBillingTransferRequest | null;
    createdAt?: string;
    // entity fields
    entityId?: number;
    workspaceId?: string;
    isBusiness?: boolean;
    iconColor?: string;
    logo?: string | null;
    closeResponsibleMembers?: CloseOverviewMember[];
    legalType?: EntityProfileType | null;
    services?: OrganizationServicesOffered[] | null;
    bookkeepingStartDate: string | null;
    // workspace + entity fields
    note?: string | null;
    // nesting
    children?: ClientsCloseOverviewRow[];
}

export function mapWorkspaceToRow(
    ws: CloseOverviewWorkspaceRow,
): ClientsCloseOverviewRow {
    return {
        id: ws.id,
        rowType: "workspace",
        type: "workspace",
        name: ws.name,
        workspaceId: ws.id.replace("workspace:", ""),
        plan: ws.plan,
        isOrganizationBillingOwner: ws.isOrganizationBillingOwner,
        isFirmWorkspace: ws.isFirmWorkspace,
        hasUsersToTransferBillingTo: ws.hasUsersToTransferBillingTo,
        orgBillingTransferRequest: ws.orgBillingTransferRequest,
        note: ws.note,
        bookkeepingStartDate: ws.bookkeepingStartDate,
        createdAt: ws.createdAt,
        services: ws.services ?? null,
        teamMembers: ws.teamMembers,
        accessMembers: ws.accessMembers,
        team: [],
        members: ws.accessMembers,
        invites: [],
        entities: ws.entities,
        children: ws.children.map((entity) =>
            mapEntityToRow(entity, ws.id.replace("workspace:", "")),
        ),
    };
}

function mapEntityToRow(
    entity: CloseOverviewEntityRow,
    workspaceId: string,
): ClientsCloseOverviewRow {
    return {
        id: entity.id,
        rowType: "entity",
        type: "entity",
        name: entity.name ?? "",
        entityId: Number(entity.id.replace("entity:", "")),
        workspaceId,
        isBusiness: entity.isBusiness,
        iconColor: entity.iconColor,
        logo: entity.logo,
        legalType: entity.legalType,
        note: entity.note,
        services: entity.services,
        teamMembers: entity.teamMembers,
        team: [],
        members: entity.teamMembers,
        invites: [],
        entities: [],
        closeResponsibleMembers: entity.closeResponsibleMembers,
        children: [],
        bookkeepingStartDate: entity.bookkeepingStartDate,
    };
}

export const clientsCloseOverviewViewConfig: ViewConfig<
    ClientsCloseOverviewRow,
    ClientsCloseOverviewColumn,
    ClientsWorkOverviewSortValue
> = {
    columns: {
        [ClientsCloseOverviewColumn.CLIENT]: {
            accessorKey: "name",
            name: "Client",
            header: "Client",
            cell: ClientNameCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.NAME_ASC,
                desc: ClientsWorkOverviewSortValue.NAME_DESC,
                defaultOrder: "asc",
            },
            immutable: true,
            minSize: 280,
            maxSize: 700,
        },
        [ClientsCloseOverviewColumn.ENTITIES]: {
            accessorKey: "entities",
            name: "Entities",
            header: "Entities",
            cell: EntitiesCell,
            minSize: 120,
            maxSize: 280,
        },
        [ClientsCloseOverviewColumn.TEAM]: {
            accessorKey: "team",
            name: "Team",
            header: "Team",
            cell: TeamCell,
            minSize: 140,
            maxSize: 180,
        },
        [ClientsCloseOverviewColumn.ACCESS]: {
            accessorKey: "members",
            name: "Members",
            header: "Members",
            cell: MembersCell,
            minSize: 150,
            maxSize: 180,
        },
        [ClientsCloseOverviewColumn.PLAN]: {
            accessorKey: "plan",
            name: "Plan",
            header: "Plan",
            cell: ClosePlanCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.PLAN_ASC,
                desc: ClientsWorkOverviewSortValue.PLAN_DESC,
            },
            minSize: 100,
            maxSize: 180,
        },
        [ClientsCloseOverviewColumn.BILLING_OWNERSHIP]: {
            accessorKey: "isOrganizationBillingOwner",
            name: "Who pays",
            header: "Who pays",
            cell: CloseBillingOwnershipCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.WHO_PAYS_ASC,
                desc: ClientsWorkOverviewSortValue.WHO_PAYS_DESC,
            },
            minSize: 100,
            maxSize: 240,
        },
        [ClientsCloseOverviewColumn.LEGAL_TYPE]: {
            accessorKey: "legalType",
            name: "Entity type",
            header: "Entity type",
            cell: CloseLegalTypeCell,
            minSize: 120,
            maxSize: 200,
        },
        [ClientsCloseOverviewColumn.NOTE]: {
            accessorKey: "note",
            name: "Memo",
            header: "Memo",
            cell: CloseNoteCell,
            minSize: 200,
            maxSize: 360,
        },
        [ClientsCloseOverviewColumn.SERVICES]: {
            accessorKey: "services",
            name: "Practices",
            header: "Practices",
            cell: CloseServicesCell,
            minSize: 200,
            maxSize: 360,
        },
        [ClientsCloseOverviewColumn.BOOKKEEPING_START_DATE]: {
            accessorKey: "bookkeepingStartDate",
            name: "Sync date",
            header: "Sync date",
            cell: BookkeepingStartDateCell,
            minSize: 140,
            maxSize: 200,
        },
        [ClientsCloseOverviewColumn.CREATED_AT]: {
            accessorKey: "createdAt",
            name: "Created at",
            header: "Created at",
            cell: CreatedAtCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.CREATED_AT_ASC,
                desc: ClientsWorkOverviewSortValue.CREATED_AT_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [ClientsCloseOverviewColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: CloseActionCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 120,
            maxSize: 160,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: ClientsCloseOverviewColumn.CLIENT,
        childrenKey: "children",
    },
};
